import React, { useState } from 'react';
import { BodyCard, Card, DollarAmount, FeeContainer, FooterCard, HeaderCard, LearnMoreOverlay, Percentage, WrapperBody, WrapperButton } from './ProgramCard.styles';
import { Button } from '@/components/Button/DefaultButton/Button';
import { ProgramInfo } from './ProgramInfo';
import { useAppSelector } from '@app/hooks';

import { pages } from '@/defaultVerbiages';
import SafeHTML from '@/utils/safeHTML';
import { Container } from '@/components/text/TextBlock.styles';

interface IProgram {
  type: string;
  name: string;
  lineAmount: string;
  estimatedApr: string;
  estimateMonthlyPmt: string;
  minLineAmount: string;
  originationFee: string;
  maxLineAmount: string;
  verbiage: string;
  interest: string;
  labelEstimatedAPR: string;
  subLabelEstimatedApr: string;
  isInfoIconEstimatedApr: boolean;
  labelEstimatedMonth: string;
  subBLabelEstimatedMonth: string;
  isInfoIconEstimatedMonth: boolean;
  labelOriginalFee: string;
  subLabelOriginalFee: string;
  isInfoIconOriginalFee: boolean;
  labelInterest: string;
  subLabelInterest: string;
  isInfoIconInterest: boolean;
  customVerbiage?: string;
}

interface IProgramCardItemProps {
  index: number;
  program: IProgram;
  totalProgram: number;
  handleModal: (label: string) => void;
  typeOfProgram: string;
  isReprice: boolean;
  handleSelect: (prog: any, id: number) => void;
  selectedProgram: number[];
  acceptCreditUnion: boolean;
  isCreditUnion: boolean;
  setSelectedProgram: (value: number[]) => void;
  customVerbiage?: string | null;
}

const ProgramCardItemComponent = ({ index, totalProgram, handleModal, typeOfProgram, isReprice, handleSelect, selectedProgram, customVerbiage = null }: IProgramCardItemProps) => {
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  const isHelocProgram = typeOfProgram === 'helocData';
  const [showDetails, setShowDetails] = useState(false);

  const extractYear = (text: string) => {
    const match = text.match(/^\d{1,2}/);
    return match ? match[0] : null;
  };

  const currentProgram = loanApplication?.availablePrograms?.[typeOfProgram]?.rates?.[index];
  const currentFee = currentProgram?.fees?.[0];

  const formattedOriginationFeeValue = currentFee ? (
    <FeeContainer>
      <DollarAmount>${currentFee.feeAmount}</DollarAmount>
      <Percentage>({currentFee.feePercentage}%)</Percentage>
    </FeeContainer>
  ) : (
    'N/A'
  );

  const isSelected = Array.isArray(selectedProgram) && selectedProgram.includes(index);

  const programName = loanApplication?.availablePrograms?.[typeOfProgram].rates[index].programName;
  const programAlias = loanApplication?.availablePrograms?.[typeOfProgram].rates[index].alias;

  return (
    <Card key={index} $isActive={isSelected}>
      <HeaderCard>
        <p>{programAlias && programAlias !== '' ? programAlias : programName}</p>
        <p>
          {index + 1} of {totalProgram}
        </p>
      </HeaderCard>
      <WrapperBody>
        {showDetails ? (
          <LearnMoreOverlay>
            <Container $isInOverlay>
              {customVerbiage ? (
                <SafeHTML html={customVerbiage} tag="p" />
              ) : (
                <>
                  <p>
                    <SafeHTML html={pages?.vmo?.vmoInfo?.learnMoreInfo.body} tag="span" />
                  </p>
                  <p>
                    <strong>{pages?.vmo?.vmoInfo?.learnMoreInfo.featuresTitle}</strong>
                    <br />
                    <SafeHTML html={pages?.vmo?.vmoInfo?.learnMoreInfo.featuresBody} tag="span" />
                  </p>
                  <p>
                    <strong>{pages?.vmo?.vmoInfo?.learnMoreInfo.benefitsTitle}</strong>
                    <br />
                    <SafeHTML html={pages?.vmo?.vmoInfo?.learnMoreInfo.benefitsBody} tag="span" />
                  </p>
                </>
              )}
            </Container>

            <Button label="Close" variant="secondary-link" onClick={() => setShowDetails(false)} $isInlineLink />
          </LearnMoreOverlay>
        ) : (
          <>
            <BodyCard>
              <ProgramInfo
                value={loanApplication?.availablePrograms?.[typeOfProgram].rates[index].apr + '%'}
                label={templateConfig?.pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.labelEstimatedAPR || 'Estimated'}
                subLabel={templateConfig?.pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.subLabelEstimatedApr || 'APR'}
                onInfoClick={() => handleModal('ESTIMATED APR')}
                isInfoIcon={
                  templateConfig?.pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.isInfoIconEstimatedApr || pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.isInfoIconEstimatedApr
                }
                isHidden={templateConfig?.pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.isHidden || pages?.vmo?.vmoV2?.programCard?.estimatedAPR?.isHidden}
              />
              <ProgramInfo
                value={'$' + Math.round(loanApplication?.availablePrograms?.[typeOfProgram].rates[index].estimatedMonthlyPayment) || '$5,008'}
                label={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.labelEstimatedMonth || 'Estimated'}
                subLabel={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.subBLabelEstimatedMonth || 'Interest Only Payment'}
                onInfoClick={() => handleModal('ESTIMATED MONTHLY PAYMENT')}
                counter="(3)"
                isInfoIcon={
                  templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.isInfoIconEstimatedMonth ||
                  pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.isInfoIconEstimatedMonth
                }
                isHidden={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.isHidden || pages?.vmo?.vmoV2?.programCard?.interestOnlyPayment?.isHidden}
              />
              {!isHelocProgram && (
                <ProgramInfo
                  value={loanApplication?.availablePrograms?.[typeOfProgram].rates[index].repaymentTile}
                  label={templateConfig?.pages?.vmo?.vmoV2?.programCard?.repaymentTile?.labelRepaymentTile || 'Year'}
                  subLabel={templateConfig?.pages?.vmo?.vmoV2?.programCard?.repaymentTile?.subLabelRepaymentTile || 'Repayment'}
                  onInfoClick={() => handleModal('REPAYMENT')}
                  isInfoIcon={
                    templateConfig?.pages?.vmo?.vmoV2?.programCard?.repaymentTile?.isInfoIconRepaymentTyle || pages?.vmo?.vmoV2?.programCard?.repaymentTile?.isInfoIconRepaymentTyle
                  }
                  isHidden={templateConfig?.pages?.vmo?.vmoV2?.programCard?.Repayment?.isHidden || pages?.vmo?.vmoV2?.programCard?.repaymentTile?.isHidden}
                />
              )}

              <>
                <ProgramInfo
                  value={formattedOriginationFeeValue}
                  label={templateConfig?.pages?.vmo?.vmoV2?.programCard?.originationFee?.labelOriginalFee || ''}
                  subLabel={templateConfig?.pages?.vmo?.vmoV2?.programCard?.originationFee?.subLabelOriginalFee || 'Origination Fee'}
                  onInfoClick={() => handleModal('ORIGINATION FEE')}
                  counter="(2)"
                  isInfoIcon={
                    templateConfig?.pages?.vmo?.vmoV2?.programCard?.originationFee?.isInfoIconOriginalFee || pages?.vmo?.vmoV2?.programCard?.originationFee?.isInfoIconOriginalFee
                  }
                  isHidden={templateConfig?.pages?.vmo?.vmoV2?.programCard?.originationFee?.isHidden || pages?.vmo?.vmoV2?.programCard?.originationFee?.isHidden}
                />
              </>

              <ProgramInfo
                value={
                  extractYear(loanApplication?.availablePrograms?.[typeOfProgram].rates[index].description) + ' yr' ||
                  Number(loanApplication?.availablePrograms?.[typeOfProgram].rates[index].term) / 12 + ' yr' ||
                  ''
                }
                label={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.labelInterest || ''}
                subLabel={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.subLabelInterest || 'Interest Only Period'}
                onInfoClick={() => handleModal('INTEREST ONLY PERIOD')}
                isInfoIcon={
                  templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.isInfoIconInterest || pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.isInfoIconInterest
                }
                isHidden={templateConfig?.pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.isHidden || pages?.vmo?.vmoV2?.programCard?.interestOnlyPeriod?.isHidden}
              />
              <WrapperButton>
                <Button
                  isDisabled={isReprice || (selectedProgram.length > 0 && !isSelected)}
                  label={isSelected && !isReprice ? 'Deselect' : 'Select'}
                  variant="primary"
                  buttonSize="s"
                  onClick={() => handleSelect(loanApplication?.availablePrograms?.[typeOfProgram].rates[index], index)}
                  $isInlineLink
                />
                <Button label="Learn More" variant="primary-link" onClick={() => setShowDetails(true)} $isInlineLink />
              </WrapperButton>
            </BodyCard>
          </>
        )}
      </WrapperBody>
      <FooterCard />
    </Card>
  );
};
export const ProgramCardItem = React.memo(ProgramCardItemComponent);
